import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { FileUploader } from "react-drag-drop-files";
import { PrimaryButton } from "../../elements/buttons";
import useUploadMedia from "../../../hooks/useUploadMedia";
import useCreateProductConcern from "../../../hooks/catalog/productConcerns/useCreateProductConcern";
import useUpdateProductConcern from "../../../hooks/catalog/productConcerns/useUpdateProductConcern";
import useGetProductConcern from "../../../hooks/catalog/productConcerns/useGetProductConcern";
import Spinner from "../../spinner";
import { toast } from "react-hot-toast";

const fileTypes = ["JPG", "PNG", "JPEG", "WEBP"];

const AddEditConcernForm = ({ isEdit = false }) => {
  const { concernId } = useParams();
  const navigate = useNavigate();

  const {
    control,
    setValue,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      media_id: "",
      image_alt: "",
      meta_title: "",
      meta_description: "",
    },
  });

  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [imageId, setImageId] = useState("");
  const [imageAlt, setImageAlt] = useState("");

  // For editing, fetch concern details
  const { data: concernData } = useGetProductConcern({
    concernId: isEdit ? concernId : null,
    onSuccess: (data) => {
      if (isEdit) {
        const resData = data?.data || {};
        setValue("name", resData.name || "");
        setValue("meta_title", resData.meta_title || "");
        setValue("meta_description", resData.meta_description || "");

        if (resData.media_id) {
          setImageId(resData.media_id);
          setImagePreview(resData.media_url || "");
          setValue("media_id", resData.media_id);
        }

        if (resData.image_alt) {
          setImageAlt(resData.image_alt);
          setValue("image_alt", resData.image_alt);
        }
      }
    },
  });

  // Image upload handlers
  const handleMediaUploadSuccess = (data) => {
    setImageId(data.data.image_id);
    setImagePreview(data.data.image_url);
    setValue("media_id", data.data.image_id);
    setImageUploading(false);
    toast.success("Image uploaded successfully", {
      position: "top-right",
      duration: 2000,
    });
  };

  const handleMediaUploadError = (error) => {
    setImageUploading(false);
    toast.error(error?.response?.data?.message || "Error uploading image", {
      position: "top-right",
      duration: 3000,
    });
  };

  const { upload } = useUploadMedia({
    url: "upload/image",
    onSuccess: handleMediaUploadSuccess,
    onError: handleMediaUploadError,
  });

  const handleImageUpload = (file) => {
    setImageUploading(true);
    upload.mutate({ thumbnail: file, screen: "CONCERN" });
  };

  const handleReUpload = () => {
    setImagePreview("");
    setImageId("");
    setValue("media_id", "");
  };

  const { handleSubmit: handleCreate } = useCreateProductConcern({
    reset: () => reset(),
  });

  const { handleSubmit: handleUpdate } = useUpdateProductConcern();

  const onSubmit = (formData) => {
    const payload = {
      ...(isEdit && { concern_id: concernId }),
      name: formData.name,
      media_id: imageId,
      image_alt: imageAlt,
      meta_title: formData.meta_title || "",
      meta_description: formData.meta_description || "",
    };

    if (isEdit) {
      handleUpdate(payload);
    } else {
      handleCreate(payload);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="py-4">
      <div className="mb-6">
        <Controller
          name="name"
          control={control}
          rules={{
            required: "Concern Name is required",
          }}
          render={({ field: { value, onChange } }) => (
            <div>
              <label htmlFor="Concern Name" className="label">
                Concern Name <small className="text-red-700">*</small>
              </label>
              <input
                required
                type="text"
                placeholder="Enter Concern Name"
                name="name"
                className="input"
                value={value}
                onChange={onChange}
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.name.message}
                </p>
              )}
            </div>
          )}
        />
      </div>

      <div className="mb-6">
        <label htmlFor="Product Image" className="label">
          Image
        </label>
        <div className="w-52 h-52 mt-2">
          {imageUploading ? (
            <div className="flex flex-col items-center justify-center h-full">
              <Spinner />
              <p className="mt-2">Uploading image...</p>
            </div>
          ) : !imagePreview ? (
            <FileUploader
              name="concern_image"
              types={fileTypes}
              handleChange={handleImageUpload}
              classes="file-uploader"
              hoverTitle="Drop here"
            />
          ) : (
            <div className="border border-dashed border-blue-700 rounded flex flex-col items-center justify-center h-full p-2">
              <div className="relative w-full h-36 flex items-center justify-center">
                <img
                  src={imagePreview}
                  alt="concern"
                  className="max-h-36 max-w-full object-contain rounded shadow"
                />
              </div>
              <div className="w-full mt-2 flex flex-col items-center">
                <input
                  type="text"
                  className="input w-full text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring focus:border-blue-400"
                  style={{ marginTop: 0 }}
                  placeholder="Image Alt Text (optional)"
                  value={imageAlt}
                  onChange={(e) => setImageAlt(e.target.value)}
                />
                <button
                  type="button"
                  className="mt-2 text-blue-600 underline text-xs"
                  onClick={handleReUpload}
                >
                  Re-Upload
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-b-2 border-black pt-2 pb-2 mb-6">
        <span className="text-2xl font-normal">Search Engine Optimization</span>
      </div>

      <div className="space-y-6 mb-6">
        <Controller
          name="meta_title"
          control={control}
          render={({ field: { value, onChange } }) => (
            <div>
              <label htmlFor="Meta Title" className="label">
                Meta Title
              </label>
              <input
                type="text"
                placeholder="Enter Meta Title"
                name="meta_title"
                className="input"
                value={value}
                onChange={onChange}
              />
            </div>
          )}
        />

        <Controller
          name="meta_description"
          control={control}
          render={({ field: { value, onChange } }) => (
            <div>
              <label htmlFor="Meta Description" className="label">
                Meta Description
              </label>
              <input
                type="text"
                placeholder="Enter Meta Description"
                name="meta_description"
                className="input"
                value={value}
                onChange={onChange}
              />
            </div>
          )}
        />
      </div>

      <div className="flex justify-end mt-8">
        <PrimaryButton label={isEdit ? "Update" : "Save"} type={"submit"} />
      </div>
    </form>
  );
};

export default AddEditConcernForm;
